import { abbrNumberFilter } from "app/filters/ngFilters";
import type {
    DynamicFilterDescriptionOption,
    DynamicFilterFieldDescription,
} from "app/pages/sales-intelligence/domain/search/types/dynamic-filter-options";
import type { IRelativeDateRangeOptions } from "app/pages/sales-intelligence/types/range";
import { assertNever } from "app/pages/sales-intelligence/utils/errors/assertions";

import type { DynamicFieldDescriptor } from "../../types/dynamicFieldDescriptor";
import type {
    DynamicComboboxFilterOption,
    DynamicComboboxFilterOptions,
    DynamicNumberRangeOptions,
    DynamicRelativeDateRangeOptions,
} from "../../types/filterOptions";

const toComboboxOptions = (values: readonly DynamicFilterDescriptionOption[]): readonly DynamicComboboxFilterOption[] =>
    values.filter((option) => option.key !== "").map((option) => ({ id: option.key, label: option.value }));

const toComboboxFilterOptions = (
    name: string,
    values: readonly DynamicFilterDescriptionOption[],
): DynamicComboboxFilterOptions => ({
    options: toComboboxOptions(values),
    label: name,
    placeholder: name,
});

const toRelativeDateRangeFilterOptions = (field: {
    readonly name: string;
    readonly values: IRelativeDateRangeOptions;
}): DynamicRelativeDateRangeOptions => ({
    ranges: field.values.ranges,
    rangeTypes: field.values.rangeTypes,
    label: field.name,
});

const toNumberRangeFilterOptions = (
    key: string,
    name: string,
    values: readonly number[],
): DynamicNumberRangeOptions => ({
    fieldName: key,
    buckets: values,
    label: name,
    formatValue: (value) => abbrNumberFilter()(value),
});

/** @throws SiError on unsuported `widget` or `dataType` */
export const toDescriptor = (field: DynamicFilterFieldDescription): DynamicFieldDescriptor | undefined => {
    switch (field.dataType) {
        case "enum": {
            const { values } = field;

            if (!values) {
                return undefined;
            }

            switch (field.widget) {
                case "combobox":
                    return {
                        key: field.key,
                        widget: "enumCombobox",
                        dataType: field.dataType,
                        options: toComboboxFilterOptions(field.name, values),
                        metadata: field.metadata,
                    };
                default:
                    return assertNever(field.widget, "descriptorTransform:enum");
            }
        }
        case "enumArray": {
            const { values } = field;

            if (!values) {
                return undefined;
            }

            switch (field.widget) {
                case "combobox":
                    return {
                        key: field.key,
                        widget: "enumArrayCombobox",
                        dataType: field.dataType,
                        options: toComboboxFilterOptions(field.name, values),
                        metadata: field.metadata,
                    };
                default:
                    return assertNever(field.widget, "descriptorTransform:enumArray");
            }
        }
        case "date":
            switch (field.widget) {
                case "combobox":
                    return {
                        key: field.key,
                        widget: "relativeDateRange",
                        dataType: field.dataType,
                        options: toRelativeDateRangeFilterOptions(field),
                        metadata: field.metadata,
                    };
                default:
                    return assertNever(field.widget, "descriptorTransform:date");
            }
        case "numberRange": {
            const { values } = field;

            if (!values) {
                return undefined;
            }

            return {
                key: field.key,
                widget: "numberRange",
                dataType: field.dataType,
                options: toNumberRangeFilterOptions(field.key, field.name, values),
                metadata: field.metadata,
            };
        }
        default:
            return assertNever(field, "descriptorTransform");
    }
};
